import {useCallback, useState} from 'react';

import type {ApprovalRequestSearchItem} from 'src/openapi/admin/models/ApprovalRequestSearchItem';

export const useRequestsTable = (requests: ApprovalRequestSearchItem[]) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
    const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

    const clearAll = useCallback(() => {
        setSelectedIds([]);
        setLastSelectedId(null);
    }, []);

    const selectPage = useCallback(() => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            requests.forEach(r => next.add(r.requestId));
            return Array.from(next);
        });
    }, [requests]);

    const toggleSelection = useCallback((id: string, shiftKey = false) => {
        if (shiftKey && lastSelectedId) {
            const currentIndex = requests.findIndex(r => r.requestId === id);
            const lastIndex = requests.findIndex(r => r.requestId === lastSelectedId);

            if (currentIndex !== -1 && lastIndex !== -1) {
                const start = Math.min(currentIndex, lastIndex);
                const end = Math.max(currentIndex, lastIndex);
                const rangeIds = requests.slice(start, end + 1).map(r => r.requestId);

                setSelectedIds(prev => {
                    const newSet = new Set(prev);
                    rangeIds.forEach(rangeId => newSet.add(rangeId));
                    return Array.from(newSet);
                });
                return;
            }
        }

        setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
        setLastSelectedId(id);
    }, [lastSelectedId, requests]);

    const setHovered = useCallback((id: string | null) => {
        setHoveredRowId(id);
    }, []);

    return {
        selectedIds,
        hoveredRowId,
        setSelectedIds,
        clearAll,
        selectPage,
        toggleSelection,
        setHovered,
    };
};
